import { useMutation, useQuery } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";

import useFCSubscriptionStore from "@hsl/fc-subscription/store";
import { type UserInput } from "@hsl/fc-subscription/types";

import { type Config } from "../../../../config/types";
import useTranslation, { Translate } from "../../../../hooks/useTranslation";
import { italianTitlesToEnglish } from "../../../../lib/italianTitles";
import { type UserDetails } from "../../../../openapi";
import Button from "../../../Button";
import Briefcase from "../../../icons/Briefcase";
import EnvelopeOpen from "../../../icons/EnvelopeOpen";
import Globe from "../../../icons/Globe";
import UserIcon from "../../../icons/User";
import Select from "../../../Select";
import Spinner from "../../../Spinner";
import { useHideTabBar } from "../../useHideTabBar";
import { Card } from "../Card";
import {
    DetailFormInput,
    InvalidInput,
} from "../Steps/AddDetails/FormComponents";
import { getProfiles } from "../Steps/AddDetails/services";

interface Props {
    user: Required<UserDetails>;
    titleNullValue?: string;
    profileNullValue?: string;
    config: Config;
    userTitles: string[];
}

export const NewUserScreen = ({
    user,
    titleNullValue = "Select",
    profileNullValue = "Select profile",
    userTitles,
    config: { audienceId, jurisdictionName, locale },
}: Props) => {
    useHideTabBar();
    const tx = useTranslation();
    const updateUser = useFCSubscriptionStore((state) => state.updateUser);

    const titleOptions = [titleNullValue, ...userTitles].map((t) => ({
        key: t,
        label: t === titleNullValue ? `- ${tx(t).result} -` : tx(t).result,
        value: t,
    }));
    const { data: profileOptions } = useQuery({
        queryKey: ["profiles", audienceId, profileNullValue],
        queryFn: async () => getProfiles(audienceId, profileNullValue),
        cacheTime: Infinity,
        refetchInterval: Infinity,
        refetchOnWindowFocus: false,
    });

    const register = useForm<UserInput>({});

    const { mutate: updateUserDetails, isLoading } = useMutation({
        mutationFn: async (data: UserInput) => {
            await updateUser({
                ...data,
                title:
                    locale === "it_IT"
                        ? italianTitlesToEnglish(data.title)
                        : data.title,
            });
        },
    });

    return (
        <div className="bg-white-smoke min-h-[50vh] w-full pb-16 pt-8">
            <div className="container mx-auto !max-w-[898px]">
                <Card className="mt-0">
                    <h2 className="my-2 text-lg font-medium">
                        <Translate>Enter your details</Translate>
                    </h2>
                    <p className="my-2">
                        <Translate>
                            Please enter your details to accept the funds that
                            have been shared with you
                        </Translate>
                        .
                    </p>
                    <form
                        id="user-details-form"
                        className="mt-4 grid grid-cols-1 gap-8 sm:grid-cols-2"
                        onSubmit={register.handleSubmit((data) =>
                            updateUserDetails(data),
                        )}
                    >
                        <div className="flex flex-col gap-8">
                            <DetailFormInput icon={<UserIcon />}>
                                <Controller
                                    name="title"
                                    control={register.control}
                                    rules={{
                                        required: tx("Please select title")
                                            .result,
                                    }}
                                    render={({ field }) => (
                                        <Select
                                            value={field.value}
                                            options={titleOptions}
                                            onChange={(value: string) => {
                                                const v =
                                                    value === titleNullValue
                                                        ? ""
                                                        : value;
                                                register.setValue("title", v);
                                            }}
                                            selectedClassName="text-base"
                                            optionClassName="text-base"
                                            buttonClassName="px-4 py-2.5 border rounded-sm border-taupe-grey"
                                        />
                                    )}
                                />
                                <InvalidInput
                                    error={register.formState.errors.title}
                                />
                                <input
                                    type="text"
                                    autoComplete="given-name"
                                    placeholder={tx("First name").result}
                                    {...register.register("firstName", {
                                        required: tx("Please enter first name")
                                            .result,
                                        maxLength: {
                                            value: 40,
                                            message: tx(
                                                "First name cannot be longer than 40 characters",
                                            ).result,
                                        },
                                    })}
                                />
                                <InvalidInput
                                    error={register.formState.errors.firstName}
                                />
                                <input
                                    type="text"
                                    autoComplete="family-name"
                                    placeholder={tx("Last name").result}
                                    {...register.register("lastName", {
                                        required: tx("Please enter last name")
                                            .result,
                                        maxLength: {
                                            value: 80,
                                            message: tx(
                                                "Last name cannot be longer than 80 characters",
                                            ).result,
                                        },
                                    })}
                                />
                                <InvalidInput
                                    error={register.formState.errors.lastName}
                                />
                            </DetailFormInput>
                            <DetailFormInput
                                icon={
                                    <EnvelopeOpen className="text-battleship-grey" />
                                }
                            >
                                <input
                                    type="email"
                                    autoComplete="email"
                                    placeholder={tx("Email").result}
                                    className="bg-white-smoke !border-white-smoke text-battleship-grey"
                                    disabled
                                    value={user.email}
                                />
                            </DetailFormInput>
                        </div>
                        <div className="flex flex-col gap-8">
                            <DetailFormInput icon={<Briefcase />}>
                                <input
                                    type="text"
                                    autoComplete="organization"
                                    placeholder={tx("Company").result}
                                    {...register.register("company", {
                                        required: tx("Please enter company")
                                            .result,
                                        maxLength: {
                                            value: 255,
                                            message: tx(
                                                "Company cannot be longer than 255 characters",
                                            ).result,
                                        },
                                    })}
                                />
                                <InvalidInput
                                    error={register.formState.errors.company}
                                />
                                <input
                                    type="text"
                                    autoComplete="job-title"
                                    placeholder={tx("Job title").result}
                                    {...register.register("jobTitle", {
                                        required: tx("Please enter job title")
                                            .result,
                                        maxLength: {
                                            value: 128,
                                            message: tx(
                                                "Job title cannot be longer than 128 characters",
                                            ).result,
                                        },
                                    })}
                                />
                                <InvalidInput
                                    error={register.formState.errors.jobTitle}
                                />
                            </DetailFormInput>
                            {profileOptions && (
                                <DetailFormInput icon={<Globe />}>
                                    <div className="flex w-full flex-col gap-4">
                                        <p className="mt-3">
                                            <Translate>
                                                {jurisdictionName}
                                            </Translate>
                                        </p>
                                        <Controller
                                            name="profile"
                                            control={register.control}
                                            rules={{
                                                required: tx(
                                                    "Please select profile",
                                                ).result,
                                            }}
                                            render={({ field }) => (
                                                <Select
                                                    value={field.value}
                                                    options={profileOptions}
                                                    onChange={(
                                                        value: string,
                                                    ) => {
                                                        const v =
                                                            value ===
                                                            profileNullValue
                                                                ? ""
                                                                : value;
                                                        register.setValue(
                                                            "profile",
                                                            v as UserInput["profile"],
                                                        );
                                                    }}
                                                    selectedClassName="text-base"
                                                    optionClassName="text-base"
                                                    buttonClassName="px-4 py-2.5 border rounded-sm border-taupe-grey"
                                                    className="z-[100]"
                                                />
                                            )}
                                        />
                                        <InvalidInput
                                            error={
                                                register.formState.errors
                                                    .profile
                                            }
                                        />
                                    </div>
                                </DetailFormInput>
                            )}
                        </div>
                    </form>
                </Card>
                <div className="mt-10 flex justify-end gap-4">
                    <Button
                        color="primary"
                        variant="large"
                        className="flex-row-reverse"
                        type="submit"
                        form="user-details-form"
                        disabled={isLoading}
                        icon={
                            isLoading ? (
                                <Spinner className="my-0 ml-4 mr-0 h-4 w-4 animate-spin" />
                            ) : undefined
                        }
                    >
                        <Translate>Save</Translate>
                    </Button>
                </div>
            </div>
        </div>
    );
};
